import { useMemo } from "react";
import { useMutationState, useQuery } from "@tanstack/react-query";

import { useCareplanContext } from "@/domains/careplan/contexts/use-careplan-context";
import { careplanQueryOptions } from "@/domains/careplan/factories/careplan-query-options";
import { createCareplanOrderModel } from "@/domains/careplan/factories/create-careplan-order-model";
import type { CareplanProductDataModel } from "@/domains/careplan/models/types";
import { CREATE_CAREPLAN_CART_MUTATION_KEY } from "@/domains/careplan/mutations/use-create-careplan-cart";
import { useCartSellingPlans } from "@/domains/careplan/queries/use-cart-selling-plans";
import { useCartStore } from "@/stores";

/**
 * The patient's order, derived from the Shopify cart. The cart itself is created by the drawer's
 * `useCareplanCart`, which is always mounted; this hook only reads it, so it is safe anywhere
 * under the careplan context.
 */
export const useCareplanOrder = () => {
  const { cartVariantIds, productsWithHistory, subscription } = useCareplanContext();
  const { data: cart, isError: isCartError } = useQuery({ ...careplanQueryOptions.cart });
  const { isError: isSellingPlansError } = useCartSellingPlans(cartVariantIds);
  const createCartStatuses = useMutationState({
    filters: { mutationKey: CREATE_CAREPLAN_CART_MUTATION_KEY },
    select: (mutation) => {
      return mutation.state.status;
    },
  });
  const recommendedCartLines = useCartStore((state) => {
    return state.recommendedCartLines;
  });

  const products = useMemo(() => {
    return productsWithHistory.filter((product): product is CareplanProductDataModel => {
      return !product.isPrevious;
    });
  }, [productsWithHistory]);

  const order = useMemo(() => {
    return cart
      ? createCareplanOrderModel({ cart, products, recommendedCartLines, subscription })
      : null;
  }, [cart, products, recommendedCartLines, subscription]);

  /*
    The cart can fail to arrive without its own query erroring: creating it can fail, and it is
    never created when the selling plans cannot load. Either way the cards stop waiting and show
    the plan's own price instead of a loading placeholder.
  */
  const isLastCreateFailed = createCartStatuses.at(-1) === "error";
  const isError = isCartError || (!cart && (isSellingPlansError || isLastCreateFailed));

  return {
    order,
    products,
    /* `null` means no cart exists yet and one is being created; `undefined` means the query is in flight. */
    isLoading: !order && !isError,
    isError,
  };
};

export const useCareplanOrderLine = (variantId: string | null) => {
  const { isError, isLoading, order, products } = useCareplanOrder();

  const line =
    variantId && order
      ? (order.lines.find((orderLine) => {
          return orderLine.variantId === variantId;
        }) ?? null)
      : null;

  return { isError, isLoading, line, order, products };
};
